import React, { useEffect, useState } from "react";
import axios from "axios";
import { Link, useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { useDispatch, useSelector } from "react-redux";
import {
  FaRegCalendarAlt,
  FaUsers,
  FaTags,
  FaUserShield,
  FaGift,
  FaHotel,
  FaUserFriends,
} from "react-icons/fa";
import { getSuperAdminDashboard, FRONTEND_URL } from "../../server/Api";
import { setSelectedEvent } from "../../redux/slices/authSlice";
import {
  EVENT_ACCESS_MODULES,
  isSuperAdminUser,
} from "../../utils/eventAccessModules";
import function1 from "../../assets/images/function1.svg";
import "../../assets/css/dashboard.css";
import "../../assets/css/event.css";

const formatDate = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleDateString();
};

const SuperAdminDashboard = () => {
  const user = useSelector((state) => state.auth.user);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [summary, setSummary] = useState(null);
  const [recentEvents, setRecentEvents] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");

  useEffect(() => {
    const fetchDashboard = async () => {
      try {
        setLoading(true);
        const res = await axios.get(getSuperAdminDashboard());
        if (res.status === 200 && res.data?.data) {
          setSummary(res.data.data.summary || {});
          setRecentEvents(res.data.data.recentEvents || []);
        }
      } catch (error) {
        console.error("Failed to load super admin dashboard:", error);
        toast.error("Failed to load dashboard data");
      } finally {
        setLoading(false);
      }
    };

    fetchDashboard();
  }, []);

  const cards = [
    {
      key: "events",
      label: "Total Events",
      value: summary?.totalEvents ?? 0,
      meta: `${summary?.activeEvents ?? 0} active`,
      icon: FaRegCalendarAlt,
      to: "/event-master",
    },
    {
      key: "users",
      label: "Total Users",
      value: summary?.totalUsers ?? 0,
      meta: `${summary?.activeUsers ?? 0} active`,
      icon: FaUsers,
    },
    {
      key: "admins",
      label: "Event Admins",
      value: summary?.eventAdmins ?? 0,
      meta: `${summary?.superAdmins ?? 0} super admin`,
      icon: FaUserShield,
    },
    {
      key: "guests",
      label: "Guests",
      value: summary?.totalGuests ?? 0,
      meta: `${summary?.totalAttendees ?? 0} attendees`,
      icon: FaUserFriends,
    },
    {
      key: "occasions",
      label: "Occasions",
      value: summary?.totalOccasions ?? 0,
      meta: "Across all events",
      icon: FaGift,
    },
    {
      key: "hotels",
      label: "Hotels",
      value: summary?.totalHotels ?? 0,
      meta: `${summary?.totalAccommodations ?? 0} accommodations`,
      icon: FaHotel,
    },
    {
      key: "lookups",
      label: "Lookups",
      value: summary?.totalLookups ?? 0,
      meta: `${summary?.totalLookupValues ?? 0} values`,
      icon: FaTags,
      to: "/lookup",
    },
  ];

  const filteredEvents = recentEvents.filter((event) =>
    String(event.eventName || "")
      .toLowerCase()
      .includes(searchTerm.toLowerCase())
  );

  const handleEnterEvent = (event) => {
    if (!isSuperAdminUser(user)) return;
    if (!event?._id) {
      toast.error("Unable to open this event.");
      return;
    }

    const allPages = EVENT_ACCESS_MODULES.map((module) => module.key);
    dispatch(
      setSelectedEvent({
        eventId: event._id,
        eventName: event.eventName,
        isAdmin: true,
        role: "Admin",
        isEventActive: event.isEventActive !== false,
        allowedPages: allPages,
        eventAllowedPages: allPages,
      })
    );
    toast.success(`Opened ${event.eventName || "event"}`);
    navigate("/dashboard", { replace: true });
  };

  return (
    <div className="container-fluid dashboard-page super-admin-dashboard">
      <div className="occasion-top d-flex justify-content-between align-items-center flex-wrap gap-2">
        <div>
          <h2 className="mb-1">Super Admin Dashboard</h2>
          <p className="sa-welcome mb-0">
            Welcome{user?.name ? `, ${user.name}` : ""}. Platform overview across
            all events.
          </p>
        </div>
      </div>

      {loading ? (
        <div className="sa-loading">Loading dashboard...</div>
      ) : (
        <>
          <div className="sa-metric-grid">
            {cards.map((card) => {
              const Icon = card.icon;
              const content = (
                <>
                  <div className="sa-metric-top">
                    <span className="sa-metric-icon">
                      <Icon />
                    </span>
                    <span className="sa-metric-label">{card.label}</span>
                  </div>
                  <div className="sa-metric-value">{card.value}</div>
                  <div className="sa-metric-meta">{card.meta}</div>
                </>
              );

              return card.to ? (
                <Link
                  key={card.key}
                  to={card.to}
                  className="sa-metric-card is-link"
                >
                  {content}
                </Link>
              ) : (
                <div key={card.key} className="sa-metric-card">
                  {content}
                </div>
              );
            })}
          </div>

          <div className="sa-events-panel">
            <div className="sa-events-head">
              <div>
                <h5 className="mb-1">Recent Events</h5>
                <p className="sa-events-sub mb-0">
                  Latest events with guest, occasion, user, and hotel counts
                </p>
              </div>
              <div className="sa-events-head-actions">
                <input
                  type="text"
                  className="form-control sa-events-search"
                  placeholder="Search events..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                />
                <Link to="/event-master" className="btn sa-events-link">
                  View all
                </Link>
              </div>
            </div>

            {filteredEvents.length > 0 ? (
              <div className="event-card-grid sa-event-card-grid">
                {filteredEvents.map((event) => {
                  const metrics = event.metrics || {};
                  const canOpen = isSuperAdminUser(user);
                  return (
                    <div
                      className={`event-card${
                        event.isEventActive === false ? " is-deactivated" : ""
                      }${canOpen ? " is-openable" : ""}`}
                      key={event._id}
                      role={canOpen ? "button" : undefined}
                      tabIndex={canOpen ? 0 : undefined}
                      onClick={
                        canOpen ? () => handleEnterEvent(event) : undefined
                      }
                      onKeyDown={
                        canOpen
                          ? (e) => {
                              if (e.key === "Enter" || e.key === " ") {
                                e.preventDefault();
                                handleEnterEvent(event);
                              }
                            }
                          : undefined
                      }
                      title={canOpen ? `Open ${event.eventName}` : undefined}
                    >
                      <div className="event-card-top">
                        <img
                          src={
                            event.eventLogo
                              ? `${FRONTEND_URL}/${event.eventLogo}`
                              : function1
                          }
                          alt=""
                          className="img-fluid-2"
                        />
                        <div className="event-card-heading">
                          <div className="event-card-title-row">
                            <strong
                              className="event-card-title"
                              title={event.eventName}
                            >
                              {event.eventName}
                            </strong>
                            {event.isEventActive === false ? (
                              <span className="event-deactivated-badge">
                                Deactivated
                              </span>
                            ) : (
                              <span className="event-active-badge">Active</span>
                            )}
                          </div>
                          {canOpen ? (
                            <p className="event-card-open-hint">
                              Click to open event
                            </p>
                          ) : null}
                        </div>
                      </div>

                      <div className="event-card-body">
                        <dl className="event-card-meta">
                          <div className="event-card-meta-item">
                            <dt className="event-card-meta-label">Host</dt>
                            <dd className="event-card-meta-value">
                              {event.hostedBy || "—"}
                            </dd>
                          </div>
                          <div className="event-card-meta-item">
                            <dt className="event-card-meta-label">Type</dt>
                            <dd className="event-card-meta-value">
                              {event.eventType || "—"}
                            </dd>
                          </div>
                          <div className="event-card-meta-item">
                            <dt className="event-card-meta-label">Dates</dt>
                            <dd className="event-card-meta-value">
                              {formatDate(event.startDate)}
                              {" – "}
                              {formatDate(event.endDate)}
                            </dd>
                          </div>
                          <div className="event-card-meta-item">
                            <dt className="event-card-meta-label">Capacity</dt>
                            <dd className="event-card-meta-value">
                              {event.capacity || "—"}
                            </dd>
                          </div>
                        </dl>

                        <div className="event-card-metrics">
                          <span>
                            <em>Guests</em>
                            <strong>{metrics.guests ?? 0}</strong>
                          </span>
                          <span>
                            <em>Occasions</em>
                            <strong>{metrics.occasions ?? 0}</strong>
                          </span>
                          <span>
                            <em>Users</em>
                            <strong>{metrics.users ?? 0}</strong>
                          </span>
                          <span>
                            <em>Hotels</em>
                            <strong>{metrics.hotels ?? 0}</strong>
                          </span>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <p className="mb-0">No events found.</p>
            )}
          </div>
        </>
      )}
    </div>
  );
};

export default SuperAdminDashboard;
